import type { ScheduledDetailedCopy } from './scheduled-page-copy-translations';

export const scheduledDetailedCopyPortuguese: Record<
  'pt-BR',
  ScheduledDetailedCopy
> = {
  'pt-BR': {
    status: {
      upcoming: 'Próxima',
      running: 'Em execução',
      waitingBackground: 'Aguardando trabalho em segundo plano',
      succeeded: 'Concluída',
      failed: 'Falhou',
      skipped: 'Ignorada',
      needsAttention: 'Requer atenção',
      unknownOutcome: 'Resultado desconhecido',
      canceled: 'Cancelada',
      malformed: 'Indisponível',
      paused: 'Pausada',
    },
    editor: {
      createTitle: 'Novo agendamento',
      editTitle: 'Editar agendamento',
      description:
        'Defina a tarefa, o horário, o contexto e a autorização para execução sem supervisão.',
      stepOf: 'Etapa {step} de 4',
      task: 'Tarefa',
      timing: 'Horário',
      execution: 'Execução',
      review: 'Revisão',
      name: 'Nome do agendamento',
      namePlaceholder: 'Por exemplo, resumo de desempenho de segunda-feira',
      instructions: 'Instruções do agente',
      instructionsPlaceholder:
        'Descreva o resultado que o agente deve produzir e onde ele pode agir.',
      sourceChat: 'Conversa de origem',
      scheduleThisChat: 'Agendar esta conversa',
      sourceSnapshot:
        'Uma cópia privada é capturada quando você salva. Alterações posteriores na conversa não afetam este agendamento.',
      sourceUnavailable:
        'A conversa de origem não está disponível ou você não tem mais acesso.',
      sourceChatLockedNote:
        'Agendamentos que reutilizam uma conversa de origem devem manter o contexto persistente e a visibilidade privada.',
      recurrence: 'Repetição',
      once: 'Uma vez',
      hourly: 'A cada hora',
      daily: 'Diariamente',
      weekdays: 'Dias úteis',
      selectedWeekdays: 'Dias da semana selecionados',
      weekly: 'Semanalmente',
      monthly: 'Mensalmente',
      date: 'Data',
      time: 'Às',
      dayOfMonth: 'Dia do mês',
      whenPlaceholder: 'Amanhã',
      whenInvalid: 'Digite uma data, como “amanhã”.',
      end: 'Termina',
      never: 'Nunca',
      onDate: 'Em uma data',
      afterOccurrences: 'Após execuções',
      occurrences: 'Execuções',
      nextRuns: 'Próximas execuções',
      schedulePreview: 'Prévia do agendamento',
      noEndDate: 'Sem data de término.',
      runsUntil: 'Executa até {date}.',
      totalRuns: '{count} execuções no total.',
      resolvingTime: 'Confirmando este horário com o servidor...',
      serverTimeRequired:
        'O servidor precisa confirmar o horário local antes de salvar.',
      contextMode: 'Contexto da conversa',
      persistent: 'Contexto persistente',
      persistentDescription: 'Reutilize a mesma conversa em cada execução.',
      isolated: 'Nova conversa a cada execução',
      isolatedDescription: 'Comece cada execução com um contexto independente.',
      visibility: 'Visibilidade',
      private: 'Privado',
      privateDescription:
        'Somente você pode ver as instruções completas, a transcrição, a atividade das ferramentas e o resultado.',
      workspace: 'Espaço de trabalho',
      workspaceWarning:
        'Compartilhar com o espaço de trabalho expõe as instruções, a transcrição, a atividade das ferramentas e o resultado.',
      creditPolicy: 'Política de créditos do espaço de trabalho',
      creditsAvailable:
        'As execuções usam o saldo de créditos e os limites atuais do espaço de trabalho.',
      creditsUnavailable:
        'Este espaço de trabalho não tem créditos disponíveis. O agendamento pode ser pausado no momento da execução.',
      fullAuthorityTitle: 'Ações do agente',
      fullAuthorityDescription:
        'Em cada execução, o agente verifica seu acesso atual e pode realizar ações destrutivas sem perguntar antes.',
      authorityAcknowledgment: 'Permitir qualquer ação sem perguntar antes.',
      authorityRequired:
        'Permita as ações do agente para criar este agendamento. As execuções não são supervisionadas, portanto ninguém poderá aprová-las depois.',
      recurrenceUnresolved:
        'Não foi possível confirmar o horário da execução. Escolha a data e a hora novamente.',
      notifications: 'Notificações no aplicativo',
      notificationsDescription:
        'Você receberá uma notificação para resultados finais que exigirem sua atenção.',
      reviewSummary:
        'Revise a tarefa e a frequência exatas antes de criar o agendamento.',
      required: 'Este campo é obrigatório.',
      invalid: 'Digite um valor válido.',
      past: 'Escolha um horário futuro.',
      endBeforeStart: 'Defina o fim do agendamento após a primeira execução.',
      draftRestored: 'Seu rascunho não salvo foi restaurado.',
      tooLong: 'Este valor é muito longo.',
      createDisabled:
        'Você não tem permissão para criar agendamentos neste espaço de trabalho.',
      saved: 'Agendamento salvo',
      unsavedTitle: 'Descartar alterações não salvas?',
      unsavedDescription:
        'As alterações neste agendamento serão perdidas se você fechar agora.',
      discardChanges: 'Descartar alterações',
    },
    detail: {
      title: 'Detalhes do agendamento',
      cadence: 'Frequência',
      owner: 'Responsável',
      timezone: 'Fuso horário',
      nextRun: 'Próxima execução',
      lastRun: 'Última execução',
      prompt: 'Tarefa',
      sourceContext: 'Contexto de origem salvo',
      executionMode: 'Modo de execução',
      recentUsage: 'Uso recente',
      credits: 'Créditos',
      tools: 'Ações',
      history: 'Histórico de execuções',
      transcript: 'Conversa',
      resultFiles: 'Arquivos de resultado',
      noResultFiles:
        'Nenhum arquivo de resultado foi salvo para esta execução.',
      noRuns: 'Nenhuma execução ainda.',
      noTranscript:
        'Ainda não há uma transcrição disponível para esta execução.',
      toolActivity: 'Atividade das ferramentas',
      toolInput: 'Detalhes da tarefa',
      toolResult: 'Resultado',
      noToolActivity:
        'Não há atividade de ferramentas disponível para esta execução.',
      loadMore: 'Carregar mais',
      preview: 'Prévia',
      sourceScheduled: 'Programada',
      sourceManual: 'Manual',
      sourceWatchdog: 'Recuperação',
      roleUser: 'Usuário',
      roleAssistant: 'Assistente',
      roleTool: 'Ferramenta',
      roleSystem: 'Sistema',
      runNow: 'Executar agora',
      pause: 'Pausar',
      resume: 'Retomar',
      share: 'Compartilhar com o espaço de trabalho',
      unshare: 'Tornar privado',
      cancelRun: 'Cancelar execução ativa',
      archive: 'Arquivar',
      deleteSchedule: 'Excluir agendamento',
      openThread: 'Ver conversa',
      confirmArchive: 'Arquivar este agendamento?',
      confirmArchiveDescription:
        'As execuções futuras serão canceladas. O histórico e os registros de auditoria serão preservados.',
      confirmDelete: 'Excluir este agendamento?',
      confirmDeleteDescription:
        'Isso remove o agendamento permanentemente. As conversas anteriores e o trabalho gerado permanecem no espaço de trabalho.',
      confirmUnshareDescription:
        'Os membros do espaço de trabalho perderão imediatamente o acesso às instruções, à transcrição, à atividade das ferramentas e ao resultado.',
      confirmCancel: 'Cancelar a execução ativa?',
      confirmCancelDescription:
        'A execução será interrompida no próximo ponto seguro de cancelamento.',
      actionCompleted: 'Agendamento atualizado',
      permissionDenied:
        'Você pode ver este agendamento, mas somente o responsável ou um administrador de agendamentos pode alterá-lo.',
      needsAttention:
        'Esta execução precisa ser revisada antes de continuar sem supervisão.',
      runDetails: 'Detalhes da execução',
    },
    notifications: {
      completed: 'Execução programada concluída',
      failed: 'A execução programada falhou',
      attention: 'Execução programada requer atenção',
      missed: 'A execução única programada foi perdida',
      viewRun: 'Ver execução',
      empty: 'Nenhuma notificação ainda',
      unread: 'Não lida',
      read: 'Lida',
      caughtUp: 'Você está em dia.',
    },
    errors: {
      generic: 'Algo deu errado. Tente novamente.',
      permission: 'Você não tem permissão para realizar esta ação.',
      disabled:
        'Os agentes programados ainda não estão habilitados para este espaço de trabalho.',
      credits: 'O espaço de trabalho não tem créditos suficientes.',
      limit: 'Este espaço de trabalho atingiu o limite de agendamentos ativos.',
      runPending: 'Este agendamento já tem uma execução em andamento.',
      invalidTime: 'Escolha um horário futuro válido.',
      notFound: 'Este agendamento ou esta execução não está mais disponível.',
    },
  },
};
